import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, ShieldCheck, CheckCircle2, XCircle, Trash2, Clock, Eye, EyeOff, Pencil,
  AlertTriangle, Filter, MapPin, Tag, RefreshCw, Phone, Mail, User, IndianRupee,
  FileText, Home, MessageCircle, Building2, List,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, resolveImage, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import AdminProjects from "./AdminProjects";
import AdminMasterPlans from "./AdminMasterPlans";
import AdminUsers from "./AdminUsers";
import { Map as MapIcon, Users as UsersIcon } from "lucide-react";

const PLACEHOLDER = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=80&w=400";

function formatTimeLeft(seconds) {
  if (seconds == null) return "—";
  if (seconds <= 0) return "Expired";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h >= 1) return `${h}h ${m}m left`;
  if (m >= 1) return `${m}m left`;
  return `${seconds}s left`;
}

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${cls}`} data-testid={`status-${status}`}>
      {status}
    </span>
  );
}

function AdminLogin({ onLoggedIn }) {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user?.role !== "admin") {
        setError("This account is not an admin account.");
        return;
      }
      onLoggedIn(user);
    } catch (err) {
      const s = err.response?.status;
      if (s === 401) setError("Wrong email or password.");
      else if (s === 429) setError("Too many failed attempts. Try again in 15 minutes.");
      else setError(formatApiError(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12" data-testid="admin-login-screen">
      <div className="w-full max-w-md bg-white rounded-card shadow-soft border border-border p-8">
        <div className="flex items-center gap-2 mb-1">
          <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center text-white">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display font-bold text-xl text-ink">Admin Panel</h1>
            <p className="text-xs text-muted-foreground">Restricted access — admin only</p>
          </div>
        </div>

        {error && (
          <div className="mt-5 text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2 flex items-start gap-2" data-testid="admin-login-error">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <Label htmlFor="admin-email">Email</Label>
            <Input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter admin email" className="mt-1.5 rounded-sm" data-testid="admin-login-email" autoComplete="email" />
          </div>
          <div>
            <Label htmlFor="admin-password">Password</Label>
            <div className="relative mt-1.5">
              <Input
                id="admin-password"
                type={showPwd ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="rounded-sm pr-10"
                data-testid="admin-login-password"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-ink"
                aria-label={showPwd ? "Hide password" : "Show password"}
                data-testid="admin-toggle-password"
                tabIndex={-1}
              >
                {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90 text-white rounded-sm h-11" data-testid="admin-login-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("listings"); // "listings" | "projects" | "master-plans" | "users"
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [acting, setActing] = useState(null); // listing id currently being acted on
  const [, setTick] = useState(0); // forces re-render every 30s so countdowns update
  const [detailsId, setDetailsId] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/listings", { params: filter === "all" ? {} : { status: filter } });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load listings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchListings(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh countdown labels every 30 seconds without re-fetching
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const approve = async (id) => {
    setActing(id);
    try {
      await api.post(`/admin/listings/${id}/approve`);
      toast.success("Listing approved & published.");
      setItems((arr) => arr.map((x) => x.id === id ? { ...x, status: "approved" } : x));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Approve failed");
    } finally { setActing(null); }
  };

  const reject = async (id) => {
    setActing(id);
    try {
      await api.post(`/admin/listings/${id}/reject`);
      toast.success("Listing rejected.");
      setItems((arr) => arr.map((x) => x.id === id ? { ...x, status: "rejected" } : x));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reject failed");
    } finally { setActing(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this listing? Its status will be set to Rejected.")) return;
    setActing(id);
    try {
      const { data } = await api.delete(`/admin/listings/${id}`);
      toast.success("Listing removed. Status set to Rejected.");
      setItems((arr) => arr.map((x) => x.id === id ? { ...x, status: data?.status || "rejected" } : x));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally { setActing(null); }
  };

  const openDetails = async (id) => {
    setDetailsId(id);
    setDetails(null);
    setDetailsLoading(true);
    try {
      const { data } = await api.get(`/admin/listings/${id}`);
      setDetails(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load details");
      setDetailsId(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => { setDetailsId(null); setDetails(null); };

  const doLogout = async () => { await logout(); navigate("/"); };

  const total = useMemo(() => (counts.pending || 0) + (counts.approved || 0) + (counts.rejected || 0), [counts]);
  const visibleItems = useMemo(() => items, [items]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center text-white">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div className="leading-none">
              <p className="font-display font-bold text-base text-ink">Admin Panel</p>
              <p className="text-[11px] text-muted-foreground -mt-0.5">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchListings} className="rounded-sm h-9 gap-1.5" data-testid="admin-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
            <Button variant="outline" onClick={() => navigate("/")} className="rounded-sm h-9" data-testid="admin-view-site">View Site</Button>
            <Button variant="ghost" onClick={doLogout} className="rounded-sm h-9 text-destructive" data-testid="admin-logout">Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Tabs — Listings / Projects */}
        <div className="mb-6 flex items-center gap-2 border-b border-border">
          <button
            onClick={() => setTab("listings")}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "listings" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-ink"}`}
            data-testid="admin-tab-listings"
          >
            <List className="h-4 w-4" /> Listings
          </button>
          <button
            onClick={() => setTab("projects")}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "projects" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-ink"}`}
            data-testid="admin-tab-projects"
          >
            <Building2 className="h-4 w-4" /> Projects
          </button>
          <button
            onClick={() => setTab("master-plans")}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "master-plans" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-ink"}`}
            data-testid="admin-tab-master-plans"
          >
            <MapIcon className="h-4 w-4" /> Master Plans
          </button>
          <button
            onClick={() => setTab("users")}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "users" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-ink"}`}
            data-testid="admin-tab-users"
          >
            <UsersIcon className="h-4 w-4" /> Users
          </button>
        </div>

        {tab === "projects" ? (
          <AdminProjects />
        ) : tab === "master-plans" ? (
          <AdminMasterPlans />
        ) : tab === "users" ? (
          <AdminUsers />
        ) : (
        <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Total" value={total} testId="stat-total" />
          <StatCard label="Pending" value={counts.pending || 0} tone="amber" testId="stat-pending" />
          <StatCard label="Approved" value={counts.approved || 0} tone="emerald" testId="stat-approved" />
          <StatCard label="Rejected" value={counts.rejected || 0} tone="rose" testId="stat-rejected" />
        </div>

        <div className="flex items-center gap-2 mb-5">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground mr-1">Filter:</span>
          {["pending", "approved", "rejected", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-full capitalize transition-colors border ${filter === s ? "bg-primary text-white border-primary" : "bg-white text-ink/70 border-border hover:bg-slate-100"}`}
            >
              {s}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
        ) : visibleItems.length === 0 ? (
          <div className="bg-white border border-border rounded-md py-16 text-center text-muted-foreground" data-testid="admin-empty">
            No <strong>{filter}</strong> listings.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="admin-listings-grid">
            {visibleItems.map((l) => {
              const img = (l.images && l.images[0]) ? resolveImage(l.images[0]) : PLACEHOLDER;
              const isPending = l.status === "pending";
              const isExpired = l.expired;
              return (
                <article key={l.id} className="bg-white rounded-md border border-border overflow-hidden flex flex-col" data-testid={`admin-listing-${l.id}`}>
                  <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                    <img src={img} alt={l.title || "Listing"} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-display font-semibold text-ink line-clamp-2">{l.title}</h3>
                      <StatusBadge status={l.status} />
                    </div>
                    <div className="text-sm text-ink/70 space-y-1 mt-1">
                      <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {[l.area, l.city].filter(Boolean).join(", ") || "—"}</p>
                      <p className="flex items-center gap-1.5"><Tag className="h-3.5 w-3.5 text-muted-foreground" /> <span className="capitalize">{l.category}</span> · <span className="text-ink font-medium">{l.price ? `₹${Number(l.price).toLocaleString("en-IN")}` : (l.priceLabel || "Contact for price")}</span></p>
                      <p className="text-xs text-muted-foreground">By {l.owner?.name || "—"} · {l.owner?.phone || ""}</p>
                    </div>

                    {isPending && (
                      <div className={`mt-3 inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md self-start ${isExpired ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`} data-testid={`time-left-${l.id}`}>
                        <Clock className="h-3.5 w-3.5" /> {formatTimeLeft(l.secondsLeft)}
                      </div>
                    )}

                    <div className="mt-auto pt-4 grid grid-cols-2 gap-2">
                      <Button onClick={() => openDetails(l.id)} variant="outline" className="rounded-sm h-9 gap-1 border-primary/40 text-primary hover:bg-primary/5" data-testid={`view-details-${l.id}`}>
                        <Eye className="h-3.5 w-3.5" /> View
                      </Button>
                      <Button onClick={() => navigate(`/dashboard/edit/${l.id}`)} variant="outline" className="rounded-sm h-9 gap-1 border-primary/40 text-primary hover:bg-primary/5" data-testid={`admin-edit-${l.id}`}>
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </Button>
                      {l.status !== "approved" && (
                        <Button onClick={() => approve(l.id)} disabled={acting === l.id} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-9 gap-1" data-testid={`approve-${l.id}`}>
                          {acting === l.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Approve
                        </Button>
                      )}
                      {l.status !== "rejected" && (
                        <Button onClick={() => reject(l.id)} disabled={acting === l.id} variant="outline" className="rounded-sm h-9 gap-1 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`reject-${l.id}`}>
                          <XCircle className="h-3.5 w-3.5" /> Reject
                        </Button>
                      )}
                      {l.status !== "rejected" && (
                        <Button onClick={() => remove(l.id)} disabled={acting === l.id} variant="ghost" className="rounded-sm h-9 gap-1 text-destructive hover:bg-destructive/10 col-span-2" data-testid={`delete-${l.id}`}>
                          <Trash2 className="h-3.5 w-3.5" /> Delete (mark Rejected)
                        </Button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
        </>
        )}
      </main>

      <Dialog open={!!detailsId} onOpenChange={(o) => { if (!o) closeDetails(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-details-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-6">
              <FileText className="h-5 w-5 text-primary" /> Listing Details
            </DialogTitle>
            <DialogDescription>Full information submitted by the seller.</DialogDescription>
          </DialogHeader>

          {detailsLoading || !details ? (
            <div className="py-14 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <div className="space-y-5 pt-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display font-bold text-lg text-ink">{details.title}</h3>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="h-3.5 w-3.5" /> {[details.area, details.city].filter(Boolean).join(", ") || "—"}</p>
                </div>
                <StatusBadge status={details.status} />
              </div>

              {details.images && details.images.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {details.images.slice(0, 6).map((img, i) => (
                    <img key={i} src={resolveImage(img) || PLACEHOLDER} alt={`Photo ${i + 1}`} className="w-full aspect-video object-cover rounded-md border border-border" />
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <InfoRow icon={<Tag className="h-3.5 w-3.5" />} label="Category" value={<span className="capitalize">{details.category}</span>} />
                <InfoRow icon={<Tag className="h-3.5 w-3.5" />} label="Subcategory" value={details.subcategory || "—"} />
                <InfoRow icon={<IndianRupee className="h-3.5 w-3.5" />} label="Price" value={details.price ? `₹${Number(details.price).toLocaleString("en-IN")} (${details.priceType})` : (details.priceLabel || "Contact for price")} />
                <InfoRow icon={<Home className="h-3.5 w-3.5" />} label="Seller Type" value={<span className="capitalize font-semibold text-primary">{details.sellerType}</span>} />
              </div>

              {details.keyDetails && Object.keys(details.keyDetails).length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Key Details</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                    {Object.entries(details.keyDetails).map(([k, v]) => (
                      <div key={k} className="bg-surface border border-border rounded-md px-3 py-2">
                        <p className="text-[11px] uppercase text-muted-foreground">{k.replace(/_/g, " ")}</p>
                        <p className="text-ink font-medium truncate">{Array.isArray(v) ? v.join(", ") : String(v)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Full Description</p>
                <p className="text-sm text-ink whitespace-pre-line bg-surface border border-border rounded-md p-3" data-testid="details-description">{details.description || "—"}</p>
              </div>

              <div className="rounded-md border border-primary/30 bg-primary/5 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">Contact (from listing form)</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div className="flex items-center gap-2"><User className="h-4 w-4 text-primary" /> <span className="font-medium text-ink">{details.contactName || "—"}</span></div>
                  <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> <a href={`tel:${details.contactPhone}`} className="font-medium text-ink hover:underline" data-testid="details-phone">{details.contactPhone || "—"}</a></div>
                  <div className="flex items-center gap-2 truncate"><Mail className="h-4 w-4 text-primary" /> <a href={`mailto:${details.contactEmail}`} className="font-medium text-ink hover:underline truncate">{details.contactEmail || "—"}</a></div>
                </div>
                {details.whatsappLink && (
                  <a href={details.whatsappLink} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#1ebe5d] hover:underline">
                    <MessageCircle className="h-3.5 w-3.5" /> Open WhatsApp chat
                  </a>
                )}
              </div>

              {details.owner && (
                <div className="rounded-md border border-border bg-white p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Posted by (account)</p>
                  <div className="text-sm space-y-1 text-ink">
                    <p><span className="text-muted-foreground">Name:</span> {details.owner.name || "—"}</p>
                    <p><span className="text-muted-foreground">Email:</span> {details.owner.email || "—"}</p>
                    <p><span className="text-muted-foreground">Account phone:</span> {details.owner.phone || "—"}</p>
                    <p><span className="text-muted-foreground">Role:</span> <span className="capitalize">{details.owner.role || "—"}</span></p>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                <Button onClick={() => { closeDetails(); navigate(`/dashboard/edit/${details.id}`); }} variant="outline" className="rounded-sm gap-1 border-primary/40 text-primary hover:bg-primary/5" data-testid="details-edit">
                  <Pencil className="h-4 w-4" /> Edit
                </Button>
                {details.status !== "approved" && (
                  <Button onClick={() => { approve(details.id); setDetails((d) => d ? { ...d, status: "approved" } : d); }} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1" data-testid="details-approve">
                    <CheckCircle2 className="h-4 w-4" /> Approve
                  </Button>
                )}
                {details.status !== "rejected" && (
                  <Button onClick={() => { reject(details.id); setDetails((d) => d ? { ...d, status: "rejected" } : d); }} variant="outline" className="rounded-sm gap-1 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="details-reject">
                    <XCircle className="h-4 w-4" /> Reject
                  </Button>
                )}
                <Button variant="ghost" onClick={closeDetails} className="rounded-sm ml-auto">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2 bg-surface border border-border rounded-md px-3 py-2">
      <span className="text-muted-foreground">{icon}</span>
      <div className="min-w-0">
        <p className="text-[11px] uppercase text-muted-foreground">{label}</p>
        <p className="text-ink font-medium truncate">{value}</p>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone, testId }) {
  const tones = {
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    rose: "text-rose-700 bg-rose-50 border-rose-200",
  };
  const cls = tones[tone] || "text-ink bg-white border-border";
  return (
    <div className={`rounded-md border p-4 ${cls}`} data-testid={testId}>
      <p className="text-xs uppercase font-semibold tracking-wide opacity-80">{label}</p>
      <p className="text-2xl font-display font-bold mt-1">{value}</p>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  }
  if (!user || user.role !== "admin") {
    return <AdminLogin onLoggedIn={() => { /* user state updates via context — Dashboard will render */ }} />;
  }
  return <AdminDashboard />;
}
